import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Hub } from 'aws-amplify/utils';
import { ratingsConfigured, ratingReader, getBookRatings, getMyRating, saveBookRating } from '../../services/bookRatings';
import './BookRating.css';

export default function BookRating({ productUrl }) {
  const [totals, setTotals] = useState(null), [summaryError, setSummaryError] = useState('');
  const [open, setOpen] = useState(false), [reader, setReader] = useState(null);
  const [saved, setSaved] = useState(null), [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [message, setMessage] = useState('');
  const [refresh, setRefresh] = useState(0);
  const root = useRef(null), trigger = useRef(null), closeButton = useRef(null);
  const epoch = useRef(0), controller = useRef(null), saving = useRef(false);
  const formId = useId();
  const dismiss = useCallback((restoreFocus = true) => {
    epoch.current += 1; controller.current?.abort(); saving.current = false;
    setOpen(false); setReader(null); setSaved(null); setSelected(null); setError(''); setMessage('');
    if (restoreFocus) trigger.current?.focus();
  }, []);
  useEffect(() => Hub.listen('auth', ({ payload }) => {
    if (['signedIn', 'signedOut', 'tokenRefresh_failure'].includes(payload.event)) dismiss(false);
  }), [dismiss]);
  useEffect(() => {
    let active = true;
    setTotals(null); setSummaryError('');
    if (ratingsConfigured() && productUrl) {
      getBookRatings(productUrl).then(value => { if (active) setTotals(value); })
        .catch(() => { if (active) setSummaryError('Ratings unavailable'); });
    }
    return () => { active = false; };
  }, [productUrl, refresh]);
  useEffect(() => {
    const current = epoch.current + 1, abort = new AbortController(); epoch.current = current;
    controller.current = abort; saving.current = false;
    setReader(null); setSaved(null); setSelected(null); setError(''); setMessage('');
    if (!open) return () => abort.abort();
    closeButton.current?.focus(); setBusy(true);
    (async () => {
      try {
        const who = await ratingReader();
        if (current !== epoch.current || abort.signal.aborted) return;
        if (who?.readerSub && who?.idToken) {
          const rating = await getMyRating(productUrl, who, abort.signal);
          if (current !== epoch.current || abort.signal.aborted) return;
          setReader(who); setSaved(rating); setSelected(rating);
        }
      } catch (failure) { if (current === epoch.current && !abort.signal.aborted) setError(failure.message); }
      finally { if (current === epoch.current && !abort.signal.aborted) setBusy(false); }
    })();
    return () => abort.abort();
  }, [open, productUrl, refresh]);
  useEffect(() => {
    if (!open) return undefined;
    const outside = event => { if (!root.current?.contains(event.target)) dismiss(false); };
    const escape = event => { if (event.key === 'Escape') { event.preventDefault(); dismiss(); } };
    document.addEventListener('pointerdown', outside); document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [open, dismiss]);
  const save = async event => {
    event.preventDefault(); if (saving.current || !reader || !selected) return;
    saving.current = true; const current = epoch.current, signal = controller.current.signal;
    setBusy(true); setError(''); setMessage('');
    try {
      const value = await saveBookRating(productUrl, reader, selected, signal);
      if (current !== epoch.current || signal.aborted) return;
      setSaved(value); setMessage('Rating saved.');
      const totals = await getBookRatings(productUrl, signal);
      if (current === epoch.current && !signal.aborted) { setTotals(totals); setSummaryError(''); }
    } catch (failure) {
      if (current === epoch.current && !signal.aborted) {
        if (failure.code === 'READER_ACCOUNT_CHANGED') { setReader(null); setSaved(null); setSelected(null); }
        setError(failure.message + ' Reload before retrying.');
      }
    } finally { if (current === epoch.current && !signal.aborted) { saving.current = false; setBusy(false); } }
  };
  if (!ratingsConfigured() || !productUrl) return null;
  const label = totals?.count ? `${totals.average.toFixed(1)} out of 5, ${totals.count} ${totals.count === 1 ? 'rating' : 'ratings'}. Rate this book` : totals ? 'Be the first to rate' : summaryError || 'Book ratings';
  return <div className="book-rating" ref={root}>
    <button type="button" className="book-rating-summary" ref={trigger} aria-label={label} aria-expanded={open} aria-controls={open ? formId : undefined}
      onClick={() => { if (open) dismiss(); else { if (summaryError) setRefresh(v => v + 1); setOpen(true); } }}>
      <span className="book-rating-dots" aria-hidden="true">{[1, 2, 3, 4, 5].map(n => <span className="book-rating-dot" key={n} style={{ '--fill': `${Math.max(0, Math.min(1, (totals?.average || 0) - n + 1)) * 100}%` }} />)}</span>
      <span className="book-rating-count">{totals?.count ? `${totals.average.toFixed(1)} (${totals.count})` : totals ? 'Be the first to rate' : summaryError || '…'}</span>
    </button>
    {open && <div id={formId} role="dialog" aria-label="Rate this book" className="book-rating-popover">
      <button type="button" className="book-rating-close" aria-label="Close rating form" ref={closeButton} onClick={() => dismiss()}>×</button>
      {busy && <p role="status">{saving.current ? 'Saving…' : 'Loading…'}</p>}
      {error && <div role="alert"><p>{error}</p><button type="button" className="book-rating-save" onClick={() => setRefresh(v => v + 1)}>Reload ratings</button></div>}
      {!busy && !error && !reader && <a className="book-rating-signin" href={`/loginreader?returnTo=${encodeURIComponent(window.location.href)}`}>Sign in as a reader to rate.</a>}
      {reader && <form onSubmit={save}>
        <fieldset disabled={busy || Boolean(error)}><legend>{saved ? `Your rating: ${saved}/5` : 'Your rating'}</legend>
          <div className="book-rating-choices">{[1, 2, 3, 4, 5].map(n => <label key={n} className="book-rating-choice">
            <input type="radio" name={formId} aria-label={`${n} out of 5`} value={n} checked={selected === n} onChange={() => { setSelected(n); setMessage(''); }} />
            <span aria-hidden="true" className={`book-rating-choice-dot${n <= selected ? ' is-filled' : ''}`} />
          </label>)}</div>
          <button type="submit" className="book-rating-save" disabled={!selected || selected === saved}>{saved ? 'Update rating' : 'Save rating'}</button>
        </fieldset>
      </form>}
      {message && <p role="status">{message}</p>}
    </div>}
  </div>;
}
